import { Component, inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from "@angular/material/dialog";
import { Visit_DetailProjection } from "@app/models/rest/visit";
import { AvailabilityInfo } from "@app/models/dto/availability-info";
import moment from "moment";
import { BookingReasonStore } from "@app/services/store/booking-reason-store";
import { BookingReason } from "@app/models/rest/booking-reason";
import { VisitStatus } from "@app/models/enums/visit-status.enum";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from "@angular/forms";
import { ValidationRule } from "@app/models/enums/validation-rule.enum";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatFormField } from "@angular/material/form-field";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/core";
import { MatInput } from "@angular/material/input";
import { MatButton } from "@angular/material/button";
import { TranslatePipe } from "@ngx-translate/core";
import { MomentModule } from "ngx-moment";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";

@Component({
  selector: "app-booking-confirm",
  templateUrl: "./booking-confirm.component.html",
  styleUrls: ["./booking-confirm.component.scss"],
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatCheckbox,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    MatDialogActions,
    MatButton,
    TranslatePipe,
    MomentModule,
    DialogHeaderComponent
  ]
})
export class BookingConfirmComponent {
  dialogRef = inject<MatDialogRef<BookingConfirmComponent>>(MatDialogRef);
  bookingReasonStore = inject(BookingReasonStore);
  fb = inject(UntypedFormBuilder);
  data = inject(MAT_DIALOG_DATA);

  visit: Visit_DetailProjection;
  availabilityInfo: AvailabilityInfo;
  schedulerBookingMessage: string;
  public forceBooking: boolean = false; // Initialize to false by default
  public reasonRequired: boolean;
  public allReasons: BookingReason[];
  public validReasons: BookingReason[];
  public loaded: boolean;
  public noteForm: UntypedFormGroup;
  public reasonVisible = false;
  public notesVisible = false;
  public referralMode = false;
  public cancellationMode = false;
  public floatingMode = false;
  public schedulerReferralMessage: string;
  public schedulerFloatingMessage: string;
  public permitForceBooking: boolean = false; // Initialize to false by default
  public ticketDurationTooLong: boolean = false;

  constructor() {
    const fb = this.fb;
    const data = this.data;

    this.visit = data.visit;
    this.referralMode = data.referralMode ? data.referralMode : false;
    this.cancellationMode = data.cancellationMode
      ? data.cancellationMode
      : false;
    this.floatingMode = data.floatingMode ? data.floatingMode : false;

    // For floating mode, always ensure force booking is disabled regardless of other settings
    if (this.floatingMode) {
      this.permitForceBooking = false;
      this.forceBooking = false;
    }

    this.availabilityInfo = data.availabilityInfo;
    this.validReasons = [];
    this.bookingReasonStore.loadAll(null, true).then((value) => {
      this.allReasons = value.resources;

      // For floating mode always treat as feasible
      let feasible = this.floatingMode
        ? true
        : this.availabilityInfo
          ? this.availabilityInfo.feasible
          : false;

      let amendment = false;
      let newBooking = false;
      if (
        this.visit.visitStatus === VisitStatus.BOOKED ||
        this.visit.visitStatus === VisitStatus.FLOATING
      ) {
        amendment = true;
      }
      if (
        this.visit.visitStatus === VisitStatus.NEW ||
        this.visit.visitStatus === VisitStatus.REFERRED ||
        this.visit.visitStatus === VisitStatus.AUTO
      ) {
        newBooking = true;
      }

      this.allReasons.forEach((reason) => {
        if (this.referralMode) {
          if (reason.referBookingReason) {
            this.validReasons.push(reason);
          }
        } else if (this.cancellationMode) {
          if (reason.cancelBookingReason) {
            this.validReasons.push(reason);
          }
        } else if (this.floatingMode) {
          // For floating mode, only include standard (non-force) booking reasons
          if (
            (newBooking &&
              reason.newBookingReason &&
              !reason.forceBookingReason) ||
            (amendment &&
              reason.amendBookingReason &&
              !reason.forceBookingReason)
          ) {
            this.validReasons.push(reason);
          }
        } else {
          // Original logic for standard booking
          if (newBooking && reason.newBookingReason) {
            if (!feasible && reason.forceBookingReason) {
              this.validReasons.push(reason);
            } else if (feasible && !reason.forceBookingReason) {
              this.validReasons.push(reason);
            }
          }
          if (amendment && reason.amendBookingReason) {
            if (!feasible && reason.forceBookingReason) {
              this.validReasons.push(reason);
            } else if (feasible && !reason.forceBookingReason) {
              this.validReasons.push(reason);
            }
          }
        }
      });
      this.loaded = true;
      if (this.validReasons.length === 0) {
        this.noteForm.controls["bookingReason"].validator = null;
        this.reasonVisible = false;
        this.noteForm.controls["notes"].validator = null;
        this.notesVisible = false;
      }
      if (this.validReasons.length === 1) {
        this.noteForm.controls["bookingReason"].setValue(this.validReasons[0]);
        this.reasonChanged({ value: this.validReasons[0] });
      }
      // this.noteForm = fb.group({
      //   bookingReason: [null, Validators.required],
      //   notes: [null, Validators.required]
      // });
    });
    this.noteForm = fb.group({
      bookingReason: [null, Validators.required],
      notes: [null, Validators.required]
    });

    if (this.availabilityInfo) {
      if (this.floatingMode) {
        // Special message for floating bookings
        this.schedulerFloatingMessage = `Flexible date range from ${moment(
          this.availabilityInfo.startTime
        )
          .parseZone()
          .format("DD/MM/YYYY")} to ${moment(this.availabilityInfo.endTime)
          .parseZone()
          .format("DD/MM/YYYY")}`;
      } else {
        // Standard appointment message
        this.schedulerBookingMessage = `${moment(
          this.availabilityInfo.startTime
        )
          .parseZone()
          .format("dddd Do MMMM")} between `;
      }
    }

    // Thursday 23rd May between 8:00am and 6:00pm
    // {{availabilityInfo.startTime | amDateFormat : 'DD/MM/YYYY HH:mm'}} to {{
    // availabilityInfo.endTime | amDateFormat : 'DD/MM/YYYY HH:mm'}}

    // For standard booking, check rank (unless overridden by other settings)
    if (!this.floatingMode) {
      this.permitForceBooking = this.availabilityInfo
        ? this.availabilityInfo.rank !== -1
        : false;
    }

    // If forceEnabled parameter was explicitly passed, respect it
    // This takes highest precedence
    if (data.hasOwnProperty("forceEnabled")) {
      this.permitForceBooking = !!data.forceEnabled; // Convert to boolean
      this.forceBooking = false; // Always reset to false when explicitly set
    }

    // When availabilityInfo is feasible, force booking should never be enabled
    if (this.availabilityInfo && this.availabilityInfo.feasible) {
      this.forceBooking = false;
    }

    if (this.visit && this.visit.ticketList) {
      this.visit.ticketList.forEach((value) => {
        if (value.duration > 600) {
          this.ticketDurationTooLong = true;
        }
      });
    }
  }

  onSelectClick() {
    console.log("select");

    // One final guard against force booking in floating mode
    const forceValue = this.floatingMode ? false : this.forceBooking;

    this.dialogRef.close({
      result: true,
      force: forceValue,
      bookingReasonId: this.noteForm.controls["bookingReason"].value
        ? this.noteForm.controls["bookingReason"].value.id
        : null,
      notes: this.noteForm.controls["notes"].value
    });
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  changeForceFlag($event: any) {
    console.log("force flag changed", $event);
  }

  compareObjects(objA: BookingReason, objB: BookingReason) {
    if (objA === null && objB === null) {
      return true;
    }
    if (objA === null || objB === null) {
      return false;
    }
    return objA.id === objB.id;
  }

  reasonChanged($event: any) {
    console.log("reason changed", $event);
    let reason: BookingReason = $event.value;
    if (reason.noteValidation === ValidationRule.REQUIRED) {
      this.noteForm.controls["notes"].setValidators(Validators.required);
      this.noteForm.controls["notes"].updateValueAndValidity();
      this.notesVisible = true;
    }
    if (reason.noteValidation === ValidationRule.OPTIONAL) {
      this.noteForm.controls["notes"].clearValidators();
      this.noteForm.controls["notes"].updateValueAndValidity();
      this.notesVisible = true;
    }
    if (reason.noteValidation === ValidationRule.HIDDEN) {
      this.noteForm.controls["notes"].clearValidators();
      this.noteForm.controls["notes"].updateValueAndValidity();
      this.notesVisible = false;
    }
    this.noteForm.updateValueAndValidity();
    console.log(this.noteForm);
  }
}
